/**
 * CardV2 - Main dispatcher component that routes to correct media layout.
 * This is the primary entry point for the new card system.
 */

import React, { FC, useMemo, useEffect } from 'react';
import { styled } from 'linaria/react';

import useTheme from 'helpers/useTheme';
import useOpenModal from 'helpers/useOpenModal';
import useThoughtsModal from 'helpers/useThoughtsModal';
import { ROUTE_ADD_THOUGHT } from 'routes';
import { COLORS } from '../../styles/constants';
import CardFooterMenu from '../UI/CardFooterMenu/CardFooterMenu';
import ReactionMenu from '../Reactions/ReactionMenu';
import ReactionsModal from '../Reactions/ReactionsModal/ReactionsModal';
import { ReactionsProvider, useReactionContextState } from '../Reactions/hooks';
import { useReactionMenu } from '../Reactions/helpers/hooks';
import { CardV2Props, AvatarData } from './types';
import { useCardData } from './hooks/useCardData';
import { CardContainer, CardHeader, CompactCardHeader, isColorDark } from './primitives';
import { BookCard, MovieCard, MusicCard, PodcastCard, StandardCard, FullBleedCard, ImmersiveCard } from './media';

const StyledFooterWrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 12px 12px 0 12px;
`;

const StyledMediaWrapper = styled.div`
  margin-top: 12px;
`;

/**
 * Get the first extraData item that has user info.
 */
function getExtraDataUser(card: CardV2Props['card']) {
  const extraDataItems = card.extraData?.items;
  if (extraDataItems && extraDataItems.length > 0) {
    const firstItem = extraDataItems[0];
    if (firstItem && 'user' in firstItem && firstItem.user) {
      return firstItem.user;
    }
  }
  return null;
}

/**
 * Extract avatar data from extraData user or owner preview.
 */
function extractAvatars(card: CardV2Props['card']): AvatarData[] {
  const avatars: AvatarData[] = [];
  const extraUser = getExtraDataUser(card);

  if (extraUser) {
    // Get avatar from userStyle.thumbnailImageSet
    const avatarUrl = extraUser.userStyle?.thumbnailImageSet?.images?.[0]?.url ?? null;
    const displayName =
      extraUser.userInfo?.firstName && extraUser.userInfo?.lastName
        ? `${extraUser.userInfo.firstName} ${extraUser.userInfo.lastName}`
        : extraUser.username;

    avatars.push({
      id: extraUser.id,
      avatarUrl,
      username: extraUser.username ?? undefined,
      name: displayName ?? undefined,
    });
  } else if (card.ownerPreview) {
    // Fallback to ownerPreview
    avatars.push({
      id: card.id,
      avatarUrl: card.ownerPreview.avatarUrl ?? null,
      username: card.ownerPreview.username ?? undefined,
      name: card.ownerPreview.displayName ?? card.ownerPreview.username ?? undefined,
    });
  }

  return avatars;
}

/**
 * Extract user display info from extraData or owner preview.
 */
function extractUserInfo(
  card: CardV2Props['card']
): {
  name: string | null;
  bio: string | null;
  isFollowing: boolean;
} {
  const extraUser = getExtraDataUser(card);

  if (extraUser) {
    const displayName =
      extraUser.userInfo?.firstName && extraUser.userInfo?.lastName
        ? `${extraUser.userInfo.firstName} ${extraUser.userInfo.lastName}`
        : extraUser.username;

    return {
      name: displayName ?? null,
      bio: extraUser.userInfo?.bio ?? null,
      isFollowing: extraUser.isFollowedByMe ?? false,
    };
  }

  // Fallback to ownerPreview
  const owner = card.ownerPreview;
  return {
    name: owner?.displayName ?? owner?.username ?? null,
    bio: null,
    isFollowing: false,
  };
}

/**
 * Extract thought/context text from card.
 * User thoughts are stored in extraData as CardCommentSchema items.
 */
function extractThought(card: CardV2Props['card']): string | null {
  const extraDataItems = card.extraData?.items;
  if (extraDataItems && extraDataItems.length > 0) {
    const firstItem = extraDataItems[0];
    // Check if it's a CardCommentSchema with text
    if (firstItem && 'text' in firstItem && firstItem.text) {
      return firstItem.text;
    }
  }
  return null;
}

/**
 * Extract collection/list title from extraData for "Added to" fallback.
 * Handles CardCommentSchema and CollectionCardLinkSchema which both have collection info.
 */
function extractListTitle(card: CardV2Props['card']): string | null {
  const extraDataItems = card.extraData?.items;
  if (extraDataItems && extraDataItems.length > 0) {
    // Check all items for collection info (CardCommentSchema or CollectionCardLinkSchema)
    for (let i = 0; i < extraDataItems.length; i += 1) {
      const item = extraDataItems[i];
      if (item && 'collection' in item && item.collection?.title) {
        return item.collection.title;
      }
    }
  }
  return null;
}

/**
 * Extract timestamp from extraData.
 */
function extractTimestamp(card: CardV2Props['card']): string | null {
  const extraDataItems = card.extraData?.items;
  if (extraDataItems && extraDataItems.length > 0) {
    const firstItem = extraDataItems[0];
    if (firstItem && 'createdAt' in firstItem && firstItem.createdAt) {
      const date = new Date(firstItem.createdAt);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays}d ago`;
      const diffWeeks = Math.floor(diffDays / 7);
      return `${diffWeeks}w ago`;
    }
  }
  return null;
}

/**
 * Extract creator/artist information from card.
 * Prefers displayCreatorNames (canonical content) over externalCreators.
 */
function extractCreatorInfo(card: CardV2Props['card']): { artist?: string; authors?: string[] } {
  // Prefer displayCreatorNames from canonical content
  if (card.displayCreatorNames && card.displayCreatorNames.length > 0) {
    const names = card.displayCreatorNames.filter((name): name is string => !!name);
    if (names.length > 0) {
      return {
        artist: names[0],
        authors: names,
      };
    }
  }

  // Fall back to externalCreators
  const creators = card.externalCreators;
  if (!creators || creators.items?.length === 0) {
    return {};
  }

  const creatorNames = creators.items
    ?.filter((c): c is NonNullable<typeof c> => c !== null)
    .map((c) => c.creatorName)
    .filter((name): name is string => !!name);

  return {
    artist: creatorNames?.[0],
    authors: creatorNames,
  };
}

/**
 * CardV2 - A unified card component that renders different layouts
 * based on media type. Supports Books, Movies/TV, Music, Podcasts,
 * and provides a default fallback for other content.
 *
 * Uses canonicalInfo for multi-source content to provide consistent
 * display across cards linked to the same canonical content.
 */
const CardV2: FC<CardV2Props> = ({
  card,
  headerVariant = 'full',
  layoutType = 'standard',
  showHeader = true,
  showFooter = true,
  className,
  dataTestId,
  onPress,
  disableAnimation,
  overlayBackgroundColor,
}) => {
  const isDarkTheme = useTheme();

  // Transform card data using canonical info with fallbacks
  const { title, imageUrl, backgroundColor, mediaType, externalLink } = useCardData(card);

  // Footer sits on the page background (no card fill), so contrast follows
  // the overlay gradient if present, otherwise the theme.
  const darkBg = overlayBackgroundColor ? isColorDark(overlayBackgroundColor) : isDarkTheme;
  const footerColor = darkBg ? '#c8c0ac' : COLORS.brown.dark['100'];

  // Reaction menu hooks
  const {
    reactionCoordinates,
    isQuickReactionOpen,
    setIsQuickReactionOpen,
    isReactionsModalOpen,
    setIsReactionsModalOpen,
    infoRef,
  } = useReactionMenu();

  const {
    setThoughtId,
    thoughtId,
    setPickerId,
    pickerId,
    cardId,
    setCardId,
    user,
    setUser,
  } = useReactionContextState();

  // Extract supplementary data
  const avatars = useMemo(() => extractAvatars(card), [card]);
  const thought = useMemo(() => extractThought(card), [card]);
  const listTitle = useMemo(() => extractListTitle(card), [card]);
  const timestamp = useMemo(() => extractTimestamp(card), [card]);
  const { name, bio, isFollowing } = useMemo(() => extractUserInfo(card), [card]);
  const { artist, authors } = useMemo(() => extractCreatorInfo(card), [card]);

  const eyebrow = useMemo(() => {
    const tags =
      card.mediaTagNames ??
      card.mediaTags?.items
        ?.filter((t): t is NonNullable<typeof t> => t !== null)
        .map((t) => t.name)
        .filter((n): n is string => !!n) ??
      [];
    if (tags.length === 0) return null;
    const tag = tags[0];
    return tag.charAt(0).toUpperCase() + tag.slice(1);
  }, [card]);

  const sourceDomain = useMemo(() => {
    const link = card.externalSources?.items?.[0]?.sourceUrl ?? null;
    if (!link) return null;
    try {
      return new URL(link).hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  }, [card]);

  // Thought click handlers — always call hooks unconditionally (Rules of Hooks)
  const openThoughtModal = useOpenModal(ROUTE_ADD_THOUGHT, true, {
    pickId: card.id,
    cardId: card.cardId,
    hasThought: !!thought,
  });

  const openThoughtsSheet = useThoughtsModal({
    id: card.id,
    openFromCardDetailPage: true,
  });

  const onThoughtClick = card.isPickedByMe ? openThoughtModal : openThoughtsSheet;

  // Set up reaction context
  useEffect(() => {
    const firstExtraData = card.extraData?.items?.[0];
    if (firstExtraData) {
      setThoughtId(firstExtraData.id ?? null);
      if ('user' in firstExtraData && firstExtraData.user) {
        setUser(firstExtraData.user);
      }
    }
    setCardId(card.id);
    setPickerId(card.pickedByUsers?.items?.[0]?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card]);

  const renderHeader = (colorOverride?: string | null) => {
    if (!showHeader || !(avatars.length > 0 || thought || timestamp || listTitle)) return null;
    const headerBg = colorOverride !== undefined ? colorOverride : overlayBackgroundColor;
    const shouldFadeThought = layoutType === 'immersive';
    return headerVariant === 'compact' ? (
      <CompactCardHeader
        avatars={avatars}
        thought={thought}
        timestamp={timestamp}
        backgroundColor={headerBg}
        onOpenReactionsModal={() => setIsReactionsModalOpen(true)}
        onThoughtClick={onThoughtClick}
      />
    ) : (
      <CardHeader
        avatars={avatars}
        name={name}
        bio={bio}
        isFollowing={isFollowing}
        thought={thought}
        timestamp={timestamp}
        listTitle={listTitle}
        backgroundColor={headerBg}
        onOpenReactionsModal={() => setIsReactionsModalOpen(true)}
        onThoughtClick={onThoughtClick}
        fadeThought={shouldFadeThought}
      />
    );
  };

  const renderFooter = (color?: string) => {
    if (!showFooter) return null;
    const resolvedColor = color ?? footerColor;
    return (
      <StyledFooterWrapper>
        <CardFooterMenu card={card} color={resolvedColor} />
      </StyledFooterWrapper>
    );
  };

  // Render the appropriate media card based on type
  const renderMediaCard = () => {
    const hasCanonical = card.canonicalInfo != null;

    // Canonical content + supported media type → specialized media card
    if (hasCanonical) {
      switch (mediaType) {
        case 'book':
          return (
            <BookCard
              title={title}
              imageUrl={imageUrl}
              backgroundColor={backgroundColor}
              externalLink={externalLink}
              authors={authors}
              disableAnimation={disableAnimation}
            />
          );
        case 'movie':
          return (
            <MovieCard
              title={title}
              imageUrl={imageUrl}
              backgroundColor={backgroundColor}
              externalLink={externalLink}
              disableAnimation={disableAnimation}
            />
          );
        case 'music':
          return (
            <MusicCard
              title={title}
              imageUrl={imageUrl}
              backgroundColor={backgroundColor}
              externalLink={externalLink}
              artist={artist}
              disableAnimation={disableAnimation}
            />
          );
        case 'podcast':
          return (
            <PodcastCard
              title={title}
              imageUrl={card.displaySecondaryImageUrl ?? imageUrl}
              backgroundColor={backgroundColor}
              externalLink={externalLink}
              showName={card.displayCreatorNames?.[0] ?? card.externalCreators?.items?.[0]?.creatorName ?? undefined}
              episodeThumbnail={card.displayThumbnailUrl ?? null}
              isVideo={false}
              disableAnimation={disableAnimation}
            />
          );
        default:
          break; // fall through to layout cards
      }
    }

    // Non-canonical or unsupported media type → layout card
    switch (layoutType) {
      case 'fullBleed':
        return (
          <FullBleedCard
            title={title}
            imageUrl={imageUrl}
            backgroundColor={backgroundColor}
            externalLink={externalLink}
            eyebrow={eyebrow}
            sourceDomain={sourceDomain}
            renderHeader={renderHeader}
            renderFooter={renderFooter}
          />
        );
      case 'immersive':
        return (
          <ImmersiveCard
            title={title}
            imageUrl={imageUrl}
            backgroundColor={backgroundColor}
            externalLink={externalLink}
            eyebrow={eyebrow}
            sourceDomain={sourceDomain}
            renderHeader={renderHeader}
            renderFooter={renderFooter}
          />
        );
      default:
        return (
          <StandardCard
            title={title}
            imageUrl={imageUrl}
            backgroundColor={backgroundColor}
            externalLink={externalLink}
            eyebrow={eyebrow}
            sourceDomain={sourceDomain}
          />
        );
    }
  };

  const layoutManagesChrome = !card.canonicalInfo && (layoutType === 'fullBleed' || layoutType === 'immersive');

  return (
    <ReactionsProvider
      value={{
        isQuickReactionOpen,
        setThoughtId,
        thoughtId,
        setPickerId,
        pickerId,
        cardId,
        setCardId,
        user,
        setUser,
      }}
    >
      <div ref={infoRef}>
        <CardContainer className={className} overlayBackgroundColor={overlayBackgroundColor} onClick={onPress}>
          <div data-testid={dataTestId}>
            {/* Header — only rendered here for standard/media cards */}
            {!layoutManagesChrome && renderHeader()}

            {/* Media-specific or layout-specific card content */}
            {headerVariant === 'compact' && !layoutManagesChrome ? (
              <StyledMediaWrapper>{renderMediaCard()}</StyledMediaWrapper>
            ) : (
              renderMediaCard()
            )}

            {/* Footer — only rendered here for standard/media cards */}
            {!layoutManagesChrome && renderFooter()}
          </div>
        </CardContainer>

        {/* Reaction Menu */}
        {reactionCoordinates && reactionCoordinates.x && reactionCoordinates.y && (
          <ReactionMenu
            isOpen={isQuickReactionOpen}
            anchorPoint={reactionCoordinates}
            onClose={() => setIsQuickReactionOpen(false)}
            onSend={() => {}}
            onEmoji={() => {}}
            onOpenReactionsModal={() => setIsReactionsModalOpen(true)}
          />
        )}

        {/* Reactions Modal */}
        {isReactionsModalOpen && <ReactionsModal id={card?.id} handleClose={() => setIsReactionsModalOpen(false)} />}
      </div>
    </ReactionsProvider>
  );
};

export default CardV2;
